import { ComponentProps, memo, useCallback, useMemo, useState } from 'react';
import { Editor } from '@tiptap/core';
import { Comment } from '@amzn/awscat-editor/dist/extensions/comment';
import { TiptapEditorView } from '@amzn/awscat-editor';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { SuggestionPopover, SuggestionPopoverTarget } from './SuggestionPopover';
import { CommentCreationPopover } from './CommentCreationPopover';
import styles from './TiptapEditorViewingMode.module.scss';
import { useTiptapEditorViewingModeViewModel } from './TiptapEditorViewingModeViewModel';

type TiptapEditorViewProps = ComponentProps<typeof TiptapEditorView>;

export interface TiptapEditorViewingModeProps extends Omit<
  TiptapEditorViewProps,
  | 'onInlineCommentClick'
  | 'comments'
  | 'editorRef'
  | 'onInlineCommentProposed'
  | 'onInlineCommentCancelled'
  | 'enableCommentProposal'
> {
  /** What inline comments to display in this editor instance, or leave undefined to display nothing. */
  readonly inlineTextFilter?: InlineTextFilter;
}

/**
 * A read-only TiptapEditorView integrated with the inline-comment subsystem.
 */
export const TiptapEditorViewingMode = memo(function TiptapEditorViewingMode(
  props: TiptapEditorViewingModeProps,
) {
  const { inlineTextFilter, ...viewProps } = props;

  // When the editor mounts, capture the instance for the view model.
  const [editor, setEditor] = useState<Editor | undefined>(undefined);
  const editorRefCallback = useCallback(
    (instance: Editor | null) => setEditor(instance ?? undefined),
    [],
  );
  const vm = useTiptapEditorViewingModeViewModel({
    inlineTextFilter,
    editor,
  });

  // When a comment highlight is clicked, forward to the VM only if commenting is enabled.
  const vmHandleInlineCommentClick = vm.handleInlineCommentClick;
  const handleInlineCommentClick = useCallback(
    (comment: Comment) => {
      if (!vm.isCommentingModeEnabled) return;
      vmHandleInlineCommentClick(comment);
    },
    [vm.isCommentingModeEnabled, vmHandleInlineCommentClick],
  );

  // When popover state changes, map it to a SuggestionPopoverTarget for the popover component.
  const popoverTarget = useMemo((): SuggestionPopoverTarget | undefined => {
    if (!vm.popover) return undefined;

    if (vm.popover.type === 'inline_comment') {
      const { commentId, target, comment, isCommentOnDeletedText } = vm.popover;
      return { type: 'doc_inline_comment', target, commentId, comment, isCommentOnDeletedText };
    }

    return undefined;
  }, [vm.popover]);

  return (
    <div className={styles.container}>
      <TiptapEditorView
        {...viewProps}
        comments={vm.editorComments}
        onInlineCommentClick={handleInlineCommentClick}
        editorRef={editorRefCallback}
        enableCommentProposal={vm.isCommentingModeEnabled}
        onInlineCommentProposed={vm.handleProposed}
        onInlineCommentCancelled={vm.handleCancelled}
        populateContentChanges={false}
      />
      {popoverTarget && (
        <SuggestionPopover
          popoverTarget={popoverTarget}
          onClose={vm.hidePopover}
          isReadonly={true}
        />
      )}
      {vm.commentProposalState && (
        <CommentCreationPopover
          target={vm.commentProposalState.target}
          textHint={vm.commentProposalState.textHint}
          onSubmit={vm.submitComment}
          onDismiss={vm.dismissProposal}
        />
      )}
    </div>
  );
});
